import { colors } from './colors';
import type { ThemeTypography } from 'types/emotion.d.ts';

interface TypographyVariantsProps {
  bodyLarge: React.CSSProperties;
  bodyMedium: React.CSSProperties;
  bodySmall: React.CSSProperties;
  bodyTiny: React.CSSProperties;

  headingLarge: React.CSSProperties;
  headingMedium: React.CSSProperties;
  headingSmall: React.CSSProperties;

  headerPopUp: React.CSSProperties;
  descriptiveSubtext: React.CSSProperties;
  mainBodyText: React.CSSProperties;
  buttonText: React.CSSProperties;
  smallTitle: React.CSSProperties;
  firstHeader: React.CSSProperties;
  secondHeader: React.CSSProperties;

  displayXl: React.CSSProperties;
  displayLg: React.CSSProperties;
  displayMd: React.CSSProperties;
  displaySm: React.CSSProperties;
  headlineXl: React.CSSProperties;
  headlineLg: React.CSSProperties;
  headlineMd: React.CSSProperties;
  headlineSm: React.CSSProperties;
  headlineXs: React.CSSProperties;
  bodyXl: React.CSSProperties;
  bodyLg: React.CSSProperties;
  bodyMd: React.CSSProperties;
  bodySm: React.CSSProperties;
  bodyXs: React.CSSProperties;
  labelMd: React.CSSProperties;
  labelSm: React.CSSProperties;
}

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TypographyVariants extends TypographyVariantsProps {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TypographyVariantsOptions extends TypographyVariantsProps {}
}

declare module '@mui/material/Typography' {
  interface TypographyOwnProps {
    textVariant?: 'regular' | 'medium' | 'semiBold' | 'bold';
  }

  interface TypographyPropsVariantOverrides extends TypographyVariantsProps {
    body1: false;
    body2: false;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
    bodyTiny: true;
    headingLarge: true;
    headingMedium: true;
    headingSmall: true;

    headerPopUp: true;
    descriptiveSubtext: true;
    mainBodyText: true;
    buttonText: true;
    smallTitle: true;
    firstHeader: true;
    secondHeader: true;

    displayXl: true;
    displayLg: true;
    displayMd: true;
    displaySm: true;
    headlineXl: true;
    headlineLg: true;
    headlineMd: true;
    headlineSm: true;
    headlineXs: true;
    bodyXl: true;
    bodyLg: true;
    bodyMd: true;
    bodySm: true;
    bodyXs: true;
    labelMd: true;
    labelSm: true;
  }
}

const fontFamily = [
  'Open Sans',
  'system-ui',
  '-apple-system',
  'Segoe UI',
  'Roboto',
  'Ubuntu',
  'Cantarell',
  'Noto Sans',
].join(',');

const fontFamilyDisplay = [
  'Gazpacho',
  'system-ui',
  '-apple-system',
  'Segoe UI',
  'Roboto',
  'Ubuntu',
  'Cantarell',
  'Noto Sans',
].join(',');

const header = {
  size1: {
    fontSize: '2rem',
    lineHeight: '3rem',
  },
  size2: {
    fontSize: '1.5rem',
    lineHeight: '2.25rem',
  },
  size3: {
    fontSize: '1.125rem',
    lineHeight: '1.688rem',
  },
};

const body = {
  size1: {
    fontSize: '1rem',
    lineHeight: '1.5',
  },
  size2: {
    fontSize: '0.875rem',
    lineHeight: '1.313rem',
  },
  size3: {
    fontSize: '0.75rem',
    lineHeight: '1.125rem',
  },
  size4: {
    fontSize: '0.5rem',
    lineHeight: '0.75rem',
  },
};

// Typography design tokens from figma styles project
// https://www.figma.com/design/0bbWLQGbqbJiOSr7i3H8vP/Styles--WIP-?node-id=137-124&vars=1&var-id=14-182&m=dev
const family = {
  display: fontFamilyDisplay,
  headline: fontFamily,
  body: fontFamily,
  label: fontFamily,
};
export const weight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};
const size = {
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  md: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.25rem', // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', // 48px
  '6xl': '3.75rem', // 60px
  '7xl': '4.5rem', // 72px
};
const lineHeight = {
  '2xs': '1rem',
  xs: '1.25rem',
  sm: '1.5rem',
  md: '2rem',
  lg: '2.5rem',
  xl: '2.75rem',
  '2xl': '3rem',
  '3xl': '3.75rem',
  '4xl': '4.5rem',
  '5xl': '5.375rem',
};

export const typography: ThemeTypography = {
  fontFamily,
  htmlFontSize: 16, // root size, 1rem = 16px
  family,
  weight,
  size,
  lineHeight,
  // Typography variants from figma styles project
  // https://www.figma.com/design/0bbWLQGbqbJiOSr7i3H8vP/Styles--WIP-?node-id=1-9&m=dev
  displayXl: {
    fontFamily: family.display,
    fontSize: size['7xl'],
    lineHeight: lineHeight['5xl'],
  },
  displayLg: {
    fontFamily: family.display,
    fontSize: size['6xl'],
    lineHeight: lineHeight['4xl'],
  },
  displayMd: {
    fontFamily: family.display,
    fontSize: size['5xl'],
    lineHeight: lineHeight['3xl'],
  },
  displaySm: {
    fontFamily: family.display,
    fontSize: size['3xl'],
    lineHeight: lineHeight.lg,
  },

  headlineXl: {
    fontFamily: family.headline,
    fontSize: size['4xl'],
  },
  headlineLg: {
    fontFamily: family.headline,
    fontSize: size['3xl'],
  },
  headlineMd: {
    fontFamily: family.headline,
    fontSize: size['2xl'],
  },
  headlineSm: {
    fontFamily: family.headline,
    fontSize: size.xl,
  },
  headlineXs: {
    fontFamily: family.headline,
    fontSize: size.lg,
  },

  bodyXl: {
    fontFamily: family.body,
    fontSize: size.xl,
  },
  bodyLg: {
    fontFamily: family.body,
    fontSize: size.lg,
  },
  bodyMd: {
    fontFamily: family.body,
    fontSize: size.md,
  },
  bodySm: {
    fontFamily: family.body,
    fontSize: size.sm,
  },
  bodyXs: {
    fontFamily: family.body,
    fontSize: size.xs,
  },

  labelMd: {
    fontFamily: family.label,
    fontSize: size.md,
  },
  labelSm: {
    fontFamily: family.label,
    fontSize: size.sm,
    lineHeight: lineHeight.xs,
  },

  // deprecated styles below
  // font styles
  headerPopUp: {
    fontSize: header.size3.fontSize,
    lineHeight: header.size3.lineHeight,
    fontWeight: 700,
    color: colors.grey[900],
  },
  descriptiveSubtext: {
    fontSize: body.size2.fontSize,
    lineHeight: body.size2.lineHeight,
    fontWeight: 400,
    color: colors.grey[500],
  },
  mainBodyText: {
    fontSize: body.size2.fontSize,
    lineHeight: body.size2.lineHeight,
    fontWeight: 400,
    color: colors.grey[700],
  },
  buttonText: {
    fontSize: body.size4.fontSize,
    lineHeight: body.size4.lineHeight,
    fontWeight: 700,
    color: colors.grey[400],
  },
  smallTitle: {
    fontSize: body.size1.fontSize,
    lineHeight: body.size1.lineHeight,
    fontWeight: 700,
    color: colors.grey[900],
  },
  firstHeader: {
    fontSize: body.size2.fontSize,
    lineHeight: body.size2.lineHeight,
    fontWeight: 700,
    color: colors.black,
  },
  secondHeader: {
    fontSize: body.size2.fontSize,
    lineHeight: body.size2.lineHeight,
    fontWeight: 700,
    color: colors.grey[600],
  },
  // Headings
  headingLarge: {
    fontSize: '3rem',
    lineHeight: '3.5rem',
  },
  headingMedium: {
    fontSize: '2.5rem',
    lineHeight: '3rem',
  },
  headingSmall: {
    fontSize: '2rem',
    lineHeight: '2.5rem',
  },
  // Body
  bodyLarge: {
    fontSize: '1.5rem',
    lineHeight: '1.25rem',
  },
  bodyMedium: {
    fontSize: '1rem',
    lineHeight: '1.25rem',
  },
  bodySmall: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  bodyTiny: {
    fontSize: '0.5rem',
    lineHeight: '0.75rem',
  },
};
